import {
  buildOverviewVendorIdSets,
  isAccountTeamVendorLabel,
  normalizeGoalTargetMonths,
  resolveOverviewVendorType,
} from "./relationshipUtils";

function mapOverviewVendor(item, vendorType) {
  const execType = String(item.executive_type || "").toLowerCase();
  const sponsorType =
    execType === "primary" || execType === "secondary" ? execType : undefined;
  return {
    id: String(item.user_id),
    name: item.user_name,
    title: item.title,
    member_title: item.title,
    role: item.role,
    label: item.label,
    vendorType,
    ladderLevel: item.grade ?? 2,
    grade: item.grade,
    grade_name: item.grade_name,
    executive_type: item.executive_type,
    ...(sponsorType ? { type: sponsorType, sponsorRole: sponsorType } : {}),
  };
}

export function overviewToRelationshipView(overview) {
  const contacts = (overview?.customer_team || []).map(contact => ({
    id: String(contact.user_id),
    name: contact.name,
    title: contact.position || contact.title || "",
    role: contact.role,
    meddpicc_role: contact.meddpicc_role || contact.role,
    meddpiccRole:
      contact.meddpicc_label || contact.meddpicc_role || contact.role,
    legacy_customer_team_member_id: String(contact.user_id),
    legacyCustomerTeamMemberId: String(contact.user_id),
    posture: "Unknown",
  }));

  const vendorIds = new Set();
  const vendors = [];
  const pushVendor = (item, vendorType) => {
    const id = String(item.user_id);
    if (vendorIds.has(id)) return;
    vendorIds.add(id);
    vendors.push(mapOverviewVendor(item, vendorType));
  };

  (overview?.account_team || []).forEach(item =>
    pushVendor(item, resolveOverviewVendorType(item.label))
  );

  const assessmentByKey = new Map(
    (overview?.assessments || []).map(item => [
      `${item.user_id}::${item.contact_id}`,
      item,
    ])
  );
  const goalByKey = new Map(
    (overview?.goals || []).map(item => [
      `${item.user_id}::${item.contact_id}`,
      item,
    ])
  );

  const { accountTeamIds, executiveIds } = buildOverviewVendorIdSets(overview);

  const pairs = (overview?.mapped_pairs || [])
    .filter(pair => pair.is_active !== false)
    .map(pair => {
      const key = `${pair.user_id}::${pair.contact_id}`;
      const assessment = assessmentByKey.get(key);
      const goal = goalByKey.get(key);
      const userId = String(pair.user_id);
      const isTeam = accountTeamIds.has(userId);
      const isExecutive = executiveIds.has(userId);
      if (!isTeam && !isExecutive) return null;
      return {
        id: String(pair.id),
        vendorId: userId,
        vendorType: isTeam ? "team_member" : "executive",
        contactId: String(pair.contact_id),
        rlScore: assessment?.rl ?? null,
        assessedPosture: assessment?.posture ?? null,
        rationale: assessment?.rationale ?? null,
        assessedAt: assessment?.assessed_at ?? null,
        targetRlScore: assessment?.target_rl ?? goal?.target_rl ?? null,
        goalTargetHorizon: assessment?.target_date ?? goal?.target_date ?? null,
        account_team_member_id: isTeam ? userId : null,
        executive_id: isTeam ? null : userId,
        pending_confirm: Boolean(pair.pending_confirm),
        preceded_by_user_id: pair.preceded_by_user_id
          ? String(pair.preceded_by_user_id)
          : null,
        preceded_by_user_name: pair.preceded_by_user_name || null,
        preceded_by_relationship_id: pair.preceded_by_relationship_id
          ? String(pair.preceded_by_relationship_id)
          : null,
        predecessor_rl: pair.predecessor_rl ?? null,
        predecessor_goal_target_rl: pair.predecessor_goal_target_rl ?? null,
        predecessor_goal_horizon_months:
          pair.predecessor_goal_horizon_months ?? null,
        predecessor_history_count: Number(pair.predecessor_history_count || 0),
      };
    })
    .filter(Boolean);

  return {
    contacts,
    vendors,
    pairs,
    source: "relationship-assessment-overview",
  };
}

export function overviewToRlSummary(overview) {
  const assessmentByKey = new Map(
    (overview?.assessments || []).map(item => [
      `${item.user_id}::${item.contact_id}`,
      item,
    ])
  );
  const goalByKey = new Map(
    (overview?.goals || []).map(item => [
      `${item.user_id}::${item.contact_id}`,
      item,
    ])
  );

  const pairs = (overview?.mapped_pairs || [])
    .filter(pair => pair.is_active !== false)
    .map(pair => {
      const key = `${pair.user_id}::${pair.contact_id}`;
      const assessment = assessmentByKey.get(key);
      const goal = goalByKey.get(key);
      return {
        pairId: String(pair.id),
        rlScore: assessment?.rl ?? null,
        assessedPosture: assessment?.posture ?? null,
        rationale: assessment?.rationale ?? null,
        assessedAt: assessment?.assessed_at ?? null,
        targetRlScore: assessment?.target_rl ?? goal?.target_rl ?? null,
        goalTargetHorizon: assessment?.target_date ?? goal?.target_date ?? null,
      };
    });

  return {
    pairs,
    rlAggregate: overview?.rl_aggregate ?? null,
  };
}

export function overviewToTeams(overview) {
  const accountTeam = [];
  const sponsors = [];

  for (const item of overview?.account_team || []) {
    const label = String(item.label || "").toLowerCase();
    const base = {
      id: String(item.user_id),
      name: item.user_name,
      member_name: item.user_name,
      role: item.role,
      title: item.title,
    };

    if (isAccountTeamVendorLabel(label)) {
      accountTeam.push(base);
    } else if (label === "executive_sponsor") {
      const execType = String(item.executive_type || "").toLowerCase();
      sponsors.push({
        ...base,
        executive_id: String(item.user_id),
        ...(execType === "primary" || execType === "secondary"
          ? { type: execType, sponsorRole: execType }
          : {}),
      });
    } else if (label === "extra_sponsor") {
      sponsors.push({
        ...base,
        executive_id: String(item.user_id),
        type: "additional",
        sponsorRole: "additional",
      });
    } else {
      sponsors.push({
        ...base,
        executive_id: String(item.user_id),
      });
    }
  }

  return {
    accountTeam,
    sponsors,
    customerTeam: (overview?.customer_team || []).map(item => {
      const memberTitle = String(item.position || item.title || "").trim();
      return {
        id: String(item.user_id),
        member_name: item.name,
        name: item.name,
        role: item.role,
        position: item.position,
        ...(memberTitle
          ? { member_title: memberTitle, title: memberTitle }
          : {}),
      };
    }),
  };
}

export function buildOverviewBundle(overview) {
  return {
    overview,
    view: overviewToRelationshipView(overview),
    summary: overviewToRlSummary(overview),
  };
}

export function buildCoverageBootstrapFromOverview(overview) {
  const bundle = buildOverviewBundle(overview);
  return {
    overview: bundle.overview,
    teamCtx: overviewToTeams(bundle.overview),
    contacts: bundle.view.contacts || [],
  };
}

export function resolvePairUsersFromOverview(overview, pairRef) {
  if (String(pairRef).includes("::")) {
    const [userId, contactId] = String(pairRef).split("::");
    return { userId, contactId };
  }

  const mapped = (overview?.mapped_pairs || []).find(
    pair => String(pair.id) === String(pairRef)
  );
  if (mapped) {
    return {
      userId: String(mapped.user_id),
      contactId: String(mapped.contact_id),
    };
  }

  const assessment = (overview?.assessments || []).find(
    item => String(item.id) === String(pairRef)
  );
  if (assessment) {
    return {
      userId: String(assessment.user_id),
      contactId: String(assessment.contact_id),
    };
  }

  return { userId: null, contactId: null };
}

export function buildAssessmentCreatePayload(userId, contactId, payload = {}) {
  const rl = Number(payload.rl_score ?? payload.rl);
  const body = {
    user_id: userId,
    contact_id: contactId,
    rl,
    posture: payload.assessed_posture ?? payload.posture ?? null,
    rationale: payload.rationale ?? null,
  };

  const targetRl = payload.target_rl ?? payload.target_rl_score;
  const targetMonths = normalizeGoalTargetMonths(
    payload.target_date ?? payload.goal_target_horizon
  );
  if (
    targetRl != null &&
    Number.isFinite(Number(targetRl)) &&
    Number(targetRl) >= rl
  ) {
    body.target_rl = Number(targetRl);
    body.status = payload.status ?? "in-progress";
    if (targetMonths != null) {
      body.target_date = targetMonths;
    }
  }

  return body;
}

export function mapAssessmentHistoryEntry(item, isCurrentPrimary) {
  const assessedAt = item.assessed_at || item.created_at;
  const assessorName = String(
    item.assessor_name || item.assessorName || ""
  ).trim();
  return {
    id: item.id,
    rlScore: item.rl ?? null,
    assessedPosture: item.posture ?? null,
    rationale: item.rationale ?? null,
    createdAt: assessedAt,
    targetRlScore: item.target_rl ?? null,
    goalTargetHorizon: normalizeGoalTargetMonths(item.target_date, assessedAt),
    status: item.status ?? null,
    assessorName: assessorName || "—",
    isCurrentPrimary,
    reassessBy: null,
  };
}

export function mapAssessmentHistoryRows(all, userId, contactId) {
  return (all || [])
    .filter(
      item =>
        userId &&
        contactId &&
        String(item.user_id) === String(userId) &&
        String(item.contact_id) === String(contactId)
    )
    .sort(
      (a, b) =>
        new Date(b.assessed_at || b.created_at).getTime() -
        new Date(a.assessed_at || a.created_at).getTime()
    )
    .map((item, index) => mapAssessmentHistoryEntry(item, index === 0));
}

export function mapRelationshipLevelRows(content) {
  const items = Array.isArray(content?.items)
    ? content.items
    : Array.isArray(content)
      ? content
      : [];
  return items.map(level => ({
    ...level,
    levels: level.level_code,
    assessment_level: level.assessment_level || "RL",
    assessmentLevel: level.assessment_level || "RL",
  }));
}
